import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Search, ShieldCheck, UserRound } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Steps,
  Textarea,
  cn,
  type StepItem,
} from '@design-system';
import { toCalendarDate } from '@shared/calculations/utils/resolveAsOfDate';
import { useDebounce } from '@/hooks/useDebounce';
import { useWizardFundingSources } from '@/pages/WizardFundingCalcPrompt/useFundingCalcPrompt';
import {
  useCreateWizardCalcSnapshotTestCase,
  useWizardCalcSnapshotClientSearch,
} from '../hooks/use-wizard-calc-snapshots';
import { getCalcSnapshotErrorMessage } from './CalcSnapshotUi';

const MIN_CLIENT_SEARCH_LENGTH = 2;

interface CalcSnapshotPinDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPinned?: (testCaseId: string) => void;
}

export function CalcSnapshotPinDialog({
  open,
  onOpenChange,
  onPinned,
}: CalcSnapshotPinDialogProps) {
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [fundingSourceId, setFundingSourceId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [asOfDate, setAsOfDate] = useState(() => toCalendarDate(new Date()));
  const debouncedClientSearch = useDebounce(clientSearch, 300).trim();
  const clientsQuery = useWizardCalcSnapshotClientSearch(debouncedClientSearch);
  const sourcesQuery = useWizardFundingSources(selectedClientId);
  const createMutation = useCreateWizardCalcSnapshotTestCase();

  const ltcPolicies = useMemo(
    () => (sourcesQuery.data?.fundingSources ?? []).filter(source => source.kind === 'ltcPolicy'),
    [sourcesQuery.data],
  );
  const selectedClient = clientsQuery.data?.find(client => client.clientId === selectedClientId);
  const visibleClients = selectedClient ? [selectedClient] : clientsQuery.data;
  const selectedPolicy = ltcPolicies.find(policy => policy.fundingSourceId === fundingSourceId);
  const isClientSearchable = clientSearch.trim().length >= MIN_CLIENT_SEARCH_LENGTH;
  const isWaitingForClientSearch = clientSearch.trim() !== debouncedClientSearch;

  useEffect(() => {
    if (ltcPolicies.length === 1 && !fundingSourceId) {
      setFundingSourceId(ltcPolicies[0].fundingSourceId);
    }
  }, [fundingSourceId, ltcPolicies]);

  useEffect(() => {
    const exactClient = clientsQuery.data?.find(client => client.clientId === clientSearch.trim());
    if (exactClient && !selectedClientId) {
      setSelectedClientId(exactClient.clientId);
    }
  }, [clientSearch, clientsQuery.data, selectedClientId]);

  const steps: StepItem[] = [
    {
      id: 'client',
      label: 'Select client',
      status: selectedClientId ? 'completed' : 'current',
    },
    {
      id: 'policy',
      label: 'Choose policy',
      status: fundingSourceId ? 'completed' : selectedClientId ? 'current' : 'upcoming',
    },
    {
      id: 'details',
      label: 'Describe test case',
      status: fundingSourceId ? 'current' : 'upcoming',
    },
  ];

  const reset = () => {
    setClientSearch('');
    setSelectedClientId('');
    setFundingSourceId('');
    setName('');
    setDescription('');
    setTags('');
    setAsOfDate(toCalendarDate(new Date()));
    createMutation.reset();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const updateClientSearch = (value: string) => {
    setClientSearch(value);
    setSelectedClientId('');
    setFundingSourceId('');
  };

  const submit = () =>
    createMutation.mutate(
      {
        name,
        description,
        sourceClientId: selectedClientId,
        fundingSourceId,
        asOfDate,
        tags: tags
          .split(',')
          .map(tag => tag.trim())
          .filter(Boolean),
      },
      {
        onSuccess: testCase => {
          handleOpenChange(false);
          onPinned?.(testCase.testCaseId);
        },
      },
    );

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DialogContent className="grid max-h-[92dvh] w-[calc(100vw-2rem)] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:max-w-6xl">
        <DialogHeader className="border-b px-6 py-5 pr-14 sm:px-8 sm:py-6">
          <DialogTitle className="text-xl">Pin calculation test case</DialogTitle>
          <DialogDescription>
            Look up an existing client and select one LTC policy.
          </DialogDescription>
          <Steps
            items={steps}
            className="pt-3"
          />
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto px-6 py-6 sm:px-8">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(340px,0.85fr)]">
            <section className="space-y-6">
              <SectionHeading
                number="1"
                title="Select the source client"
                description="Search the existing client database. Only clients with an LTC policy can be pinned."
              />

              <Field label="Search by name, email, or client ID">
                <div className="relative">
                  <Search className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2" />
                  <Input
                    value={clientSearch}
                    onChange={event => updateClientSearch(event.target.value)}
                    className="h-11 pl-10"
                    aria-label="Search clients by name, email, or client ID"
                    autoFocus
                  />
                </div>
              </Field>

              <div className="min-h-32 rounded-xl border bg-gray-50/50 p-2">
                {!isClientSearchable ? (
                  <SearchPrompt />
                ) : !selectedClient && (isWaitingForClientSearch || clientsQuery.isFetching) ? (
                  <div className="text-muted-foreground flex min-h-28 items-center justify-center gap-2 text-sm">
                    <Loader2 className="size-4 animate-spin" />
                    Searching clients…
                  </div>
                ) : clientsQuery.error ? (
                  <p className="flex min-h-28 items-center justify-center px-4 text-center text-sm text-red-700">
                    {getCalcSnapshotErrorMessage(clientsQuery.error)}
                  </p>
                ) : visibleClients?.length === 0 ? (
                  <p className="text-muted-foreground flex min-h-28 items-center justify-center text-sm">
                    No clients found.
                  </p>
                ) : (
                  <div className="max-h-64 space-y-2 overflow-y-auto">
                    {visibleClients?.map(client => {
                      const fullName =
                        [client.clientFirstName, client.clientLastName].filter(Boolean).join(' ') ||
                        'Unnamed client';
                      const hasLtcPolicy = client.ltcPolicyCount > 0;
                      const selected = selectedClientId === client.clientId;

                      return (
                        <button
                          type="button"
                          key={client.clientId}
                          disabled={!hasLtcPolicy}
                          aria-pressed={selected}
                          onClick={() => {
                            setSelectedClientId(client.clientId);
                            setFundingSourceId('');
                          }}
                          className={cn(
                            'flex w-full items-start gap-3 rounded-lg border bg-white p-3 text-left transition-colors',
                            hasLtcPolicy && 'hover:border-primary-300 hover:bg-primary-50/40',
                            selected && 'border-primary-400 bg-primary-50 ring-primary-100 ring-2',
                            !hasLtcPolicy && 'cursor-not-allowed bg-gray-50',
                          )}
                        >
                          <span
                            className={cn(
                              'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full',
                              selected ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600',
                            )}
                          >
                            {selected ? (
                              <CheckCircle2 className="size-4" />
                            ) : (
                              <UserRound className="size-4" />
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-medium text-gray-950">{fullName}</span>
                            <span className="text-muted-foreground block truncate text-xs">
                              {client.clientEmail || 'No email'} · {client.clientId}
                            </span>
                          </span>
                          <Badge variant={hasLtcPolicy ? 'success' : 'secondary'}>
                            {client.ltcPolicyCount} LTC{' '}
                            {client.ltcPolicyCount === 1 ? 'policy' : 'policies'}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <SectionHeading
                  number="2"
                  title="Choose the policy"
                  description="The frozen snapshot will cover this policy only."
                />

                {!selectedClientId ? (
                  <div className="text-muted-foreground rounded-xl border border-dashed p-6 text-center text-sm">
                    Select a client to load their LTC policies.
                  </div>
                ) : sourcesQuery.isFetching ? (
                  <div className="text-muted-foreground flex items-center gap-2 rounded-xl border p-5 text-sm">
                    <Loader2 className="size-4 animate-spin" />
                    Loading policies…
                  </div>
                ) : sourcesQuery.error ? (
                  <p className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {getCalcSnapshotErrorMessage(sourcesQuery.error)}
                  </p>
                ) : ltcPolicies.length === 0 ? (
                  <p className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                    This client has no LTC policy available to pin.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {ltcPolicies.map(policy => {
                      const selected = policy.fundingSourceId === fundingSourceId;
                      return (
                        <button
                          key={policy.fundingSourceId}
                          type="button"
                          aria-pressed={selected}
                          onClick={() => setFundingSourceId(policy.fundingSourceId)}
                          className={cn(
                            'hover:border-primary-300 flex w-full items-start gap-3 rounded-xl border bg-white p-4 text-left transition-colors',
                            selected && 'border-primary-400 bg-primary-50 ring-primary-100 ring-2',
                          )}
                        >
                          <span className="bg-primary-100 text-primary-dark mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full">
                            <ShieldCheck className="size-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-medium text-gray-950">
                              {policy.primaryLabel}
                            </span>
                            <span className="text-muted-foreground mt-1 block break-words text-xs">
                              {policy.secondaryLabel}
                            </span>
                          </span>
                          {selected && (
                            <CheckCircle2 className="text-primary mt-1 size-5 shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

            <section className="space-y-6 lg:border-l lg:pl-8">
              <SectionHeading
                number="3"
                title="Describe the test case"
                description="Give QA enough context to understand the scenario and why it exists."
              />

              <Field label="Name">
                <Input
                  value={name}
                  onChange={event => setName(event.target.value)}
                  className="h-11"
                />
              </Field>

              <Field label="Description">
                <Textarea
                  value={description}
                  onChange={event => setDescription(event.target.value)}
                  className="min-h-28 resize-none"
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                <Field label="As-of date">
                  <Input
                    type="date"
                    value={asOfDate}
                    onChange={event => setAsOfDate(event.target.value)}
                    className="h-11"
                  />
                </Field>
                <Field label="Tags (comma separated)">
                  <Input
                    value={tags}
                    onChange={event => setTags(event.target.value)}
                    className="h-11"
                  />
                </Field>
              </div>

              <div className="rounded-xl border bg-gray-50 p-4">
                <p className="text-sm font-medium text-gray-950">What happens when you pin</p>
                <p className="text-muted-foreground mt-1 text-sm leading-6">
                  The current inputs and calculation output are frozen, then replayed immediately as
                  a self-check before the test case is saved.
                </p>
              </div>

              {createMutation.error && (
                <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {getCalcSnapshotErrorMessage(createMutation.error)}
                </p>
              )}
            </section>
          </div>
        </div>

        <DialogFooter className="items-center border-t bg-white px-6 py-4 sm:justify-between sm:px-8">
          <div className="text-muted-foreground hidden min-w-0 text-left text-xs sm:block">
            {selectedClient ? (
              <>
                <span className="font-medium text-gray-800">
                  {[selectedClient.clientFirstName, selectedClient.clientLastName]
                    .filter(Boolean)
                    .join(' ')}
                </span>
                {selectedPolicy ? ` · ${selectedPolicy.primaryLabel}` : ' · Select a policy'}
              </>
            ) : (
              'No client selected'
            )}
          </div>
          <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row">
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={
                !name.trim() || !selectedClientId || !fundingSourceId || createMutation.isPending
              }
            >
              {createMutation.isPending && <Loader2 className="size-4 animate-spin" />}
              Pin and self-check
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionHeading({
  number,
  title,
  description,
}: {
  number: string;
  title: string;
  description: string;
}) {
  return (
    <div className="flex gap-3">
      <span className="bg-primary-100 text-primary-dark flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
        {number}
      </span>
      <div>
        <h3 className="font-semibold text-gray-950">{title}</h3>
        <p className="text-muted-foreground mt-1 text-sm">{description}</p>
      </div>
    </div>
  );
}

function SearchPrompt() {
  return (
    <div className="text-muted-foreground flex min-h-28 flex-col items-center justify-center gap-2 text-center text-sm">
      <Search className="size-5" />
      <p>Type at least {MIN_CLIENT_SEARCH_LENGTH} characters.</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-2 text-sm font-medium text-gray-800">
      <span>{label}</span>
      {children}
    </label>
  );
}
